/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

// Project Components
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

// Components
import {
    Box,
    Divider,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    // Hooks
    useBreakpointValue,
    useMultiStyleConfig,
    List,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AuthHelpers, useAuthHelper, useCustomerType} from '@salesforce/commerce-sdk-react'
// Icons
// import {
//     BrandLogo,
//     SignoutIcon,
//     StoreIcon,
//     UserIcon
// } from '@salesforce/retail-react-app/app/components/icons'

// Others
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
//import {getPathWithLocale, categoryUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'

import {ListMenuContent} from '../list-menu/list-menu-content'
import {getControllerUrl} from '../../utills/controllerUrlGenerator'
import sites from '../../../../config/sites'

import {ListMenuContentInner} from '../list-menu/list-menu-content-inner'
import {MOBILE_NAV_INTIAL_DEATURE_IMAGE} from '../../constants'
import {Link as RouteLink} from 'react-router-dom'
import {TrackOrderModal, useTrackOrderModal} from '../../hooks/use-track-order-modal'
// The FONT_SIZES and FONT_WEIGHTS constants are used to control the styling for
// the accordion buttons as their current depth. In the below definition we assign
// values for depths 0 - 3, any depth deeper than that will use the default styling.
const PHONE_DRAWER_SIZE = 'xs'
const TABLET_DRAWER_SIZE = 'lg'

/**
 * This is the navigation component used for mobile devices (phone and tablet). It's
 * main usage is to navigate from one category to the next, but also homes links to
 * support, log in and out actions, as support links.
 */
const DrawerMenu = ({
    root,
    itemsKey,
    itemComponent,
    isOpen,
    onClose = noop,
    menuToggle,
    setMenuToggle,
    isStickyActive,
    promoBannerInHeaderIsActive
}) => {
    const intl = useIntl()
    const {isRegistered} = useCustomerType()
    const styles = useMultiStyleConfig('DrawerMenu')
    const navigate = useNavigation()
    const authModal = useAuthModal()
    const trackOrderModal = useTrackOrderModal()
    const drawerSize = useBreakpointValue({sm: PHONE_DRAWER_SIZE, md: TABLET_DRAWER_SIZE})
    const socialIconVariant = useBreakpointValue({base: 'flex', md: 'flex-start'})
    const {site, buildUrl} = useMultiSite()
    const {l10n} = site
    const [showLoading, setShowLoading] = useState(false)
    const [ariaBusy, setAriaBusy] = useState('true')
    const logout = useAuthHelper(AuthHelpers.Logout)
    const onSignoutClick = async () => {
        await logout.mutateAsync()
        setMenuToggle(!menuToggle)
        // navigate('/login')
        window.location.href = '/login/'
    }
    const MainContentComponent = itemComponent

    const mobileFeatureImage = `<img src=${getAssetUrl(
        MOBILE_NAV_INTIAL_DEATURE_IMAGE
    )} loading="lazy" alt="cavender home" title="cavender home">`

    const [featureImage, setfeatureImage] = useState(mobileFeatureImage)
    const [featureImageMobileLevelTwo, setFeatureImageMobileLevelTwo] = useState('')
    const [featureImageMobileLevelThree, setFeatureImageMobileLevelThree] = useState('')
    const [featureImageMobileLevelFour, setFeatureImageMobileLevelFour] = useState('')
    const [selectedLevelOneMenu, setSelectedLevelOneMenu] = useState({})
    const [linksForLevelTwoMenu, setLinksForLevelTwoMenu] = useState([])
    const [selectedLevelTwoMenu, setSelectedLevelTwoMenu] = useState({})
    const [linksForLevelThreeMenu, setLinksForLevelThreeMenu] = useState([])
    const [isLevelTwoMenuOpen, setIsLevelTwoMenuOpen] = useState(false)
    const [isLevelThreeMenuOpen, setIsLevelThreeMenuOpen] = useState(false)
    const [linksForContentComp, setLinksForContentComp] = useState({})

    const supportedLocaleIds = l10n?.supportedLocales.map((locale) => locale.id)
    const showLocaleSelector = supportedLocaleIds?.length > 1

    const handleClose = () => {
        onClose()
        setMenuToggle(!menuToggle)
    }

    useEffect(() => {
        setAriaBusy('false')
    }, [])

    const handleOnAccountClick = () => {
        handleClose()
        // setMenuToggle((toggle) => !toggle)
        authModal.onOpen()
    }

    const handleTrackOrderClick = (e) => {
        e?.preventDefault()
        e?.stopPropagation()
        if (isRegistered) {
            const path = buildUrl('/account/orders')
            navigate(path)
        } else {
            handleClose()
            trackOrderModal.onOpen()
        }
    }

    return (
        <>
            <Drawer
                isOpen={isOpen}
                onClose={handleClose}
                placement="left"
                size={drawerSize}
                isFullHeight={false}
            >
                <DrawerOverlay {...styles.drawerOverlay}>
                    <DrawerContent
                        {...(isStickyActive
                            ? styles.drawerContentSticky
                            : !promoBannerInHeaderIsActive
                            ? styles.drawerContentWithoutPromoBanner
                            : styles.drawerContent)}
                    >
                        {/* Main Content */}
                        <DrawerBody {...styles.drawerBody}>
                            <div
                                id="category-nav"
                                aria-live="polite"
                                aria-busy={ariaBusy}
                                aria-atomic="true"
                                style={{paddingRight: '10px'}}
                            >
                                {showLoading && <LoadingSpinner />}

                                {/* level 1 */}
                                {root?.[itemsKey] &&
                                    !Object.keys(linksForContentComp)?.length > 0 && (
                                        <>
                                            <MainContentComponent
                                                item={root}
                                                itemsKey={itemsKey}
                                                // initialFocusRef={initialFocusRef}
                                                isMobileMenu={true}
                                                onClose={onClose}
                                                maxColumns={'2'}
                                                setfeatureImage={setfeatureImage}
                                                selectedLevelOneMenu={selectedLevelOneMenu}
                                                selectedLevelTwoMenu={selectedLevelTwoMenu}
                                                setSelectedLevelOneMenu={setSelectedLevelOneMenu}
                                                setLinksForLevelTwoMenu={setLinksForLevelTwoMenu}
                                                setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                                setIsLevelThreeMenuOpen={setIsLevelThreeMenuOpen}
                                                isLevelTwoMenuOpen={isLevelTwoMenuOpen}
                                                isLevelThreeMenuOpen={isLevelThreeMenuOpen}
                                                setLinksForContentComp={setLinksForContentComp}
                                                setFeatureImageMobileLevelTwo={
                                                    setFeatureImageMobileLevelTwo
                                                }
                                                setFeatureImageMobileLevelThree={
                                                    setFeatureImageMobileLevelThree
                                                }
                                                setFeatureImageMobileLevelFour={
                                                    setFeatureImageMobileLevelFour
                                                }
                                            />
                                        </>
                                    )}

                                {/* level 2 */}
                                {Object.keys(linksForContentComp)?.length > 0 &&
                                    !linksForLevelTwoMenu?.length > 0 && (
                                        <>
                                            {Object.keys(linksForContentComp)?.length > 0 && (
                                                <ListMenuContent
                                                    item={linksForContentComp}
                                                    itemsKey={itemsKey}
                                                    // initialFocusRef={initialFocusRef}
                                                    isMobileMenu={true}
                                                    setMenuToggle={setMenuToggle}
                                                    onClose={onClose}
                                                    maxColumns={'2'}
                                                    setfeatureImage={setfeatureImage}
                                                    selectedLevelOneMenu={selectedLevelOneMenu}
                                                    selectedLevelTwoMenu={selectedLevelTwoMenu}
                                                    setSelectedLevelOneMenu={
                                                        setSelectedLevelOneMenu
                                                    }
                                                    setSelectedLevelTwoMenu={
                                                        setSelectedLevelTwoMenu
                                                    }
                                                    setLinksForLevelThreeMenu={
                                                        setLinksForLevelThreeMenu
                                                    }
                                                    setLinksForLevelTwoMenu={
                                                        setLinksForLevelTwoMenu
                                                    }
                                                    setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                                    setIsLevelThreeMenuOpen={
                                                        setIsLevelThreeMenuOpen
                                                    }
                                                    isLevelTwoMenuOpen={isLevelTwoMenuOpen}
                                                    isLevelThreeMenuOpen={isLevelThreeMenuOpen}
                                                    setLinksForContentComp={setLinksForContentComp}
                                                    setFeatureImageMobileLevelTwo={
                                                        setFeatureImageMobileLevelTwo
                                                    }
                                                    setFeatureImageMobileLevelThree={
                                                        setFeatureImageMobileLevelThree
                                                    }
                                                    setFeatureImageMobileLevelFour={
                                                        setFeatureImageMobileLevelFour
                                                    }
                                                />
                                            )}
                                        </>
                                    )}

                                {/* level 3 */}
                                {linksForLevelTwoMenu?.length > 0 &&
                                    !linksForLevelThreeMenu?.length > 0 && (
                                        <>
                                            {linksForLevelTwoMenu?.length > 0 && (
                                                <ListMenuContentInner
                                                    items={linksForLevelTwoMenu}
                                                    onClose={onClose}
                                                    isMobileMenu={true}
                                                    setMenuToggle={setMenuToggle}
                                                    setfeatureImage={setfeatureImage}
                                                    selectedLevelOneMenu={selectedLevelOneMenu}
                                                    selectedLevelTwoMenu={selectedLevelTwoMenu}
                                                    setSelectedLevelTwoMenu={
                                                        setSelectedLevelTwoMenu
                                                    }
                                                    setLinksForLevelThreeMenu={
                                                        setLinksForLevelThreeMenu
                                                    }
                                                    setLinksForLevelTwoMenu={
                                                        setLinksForLevelTwoMenu
                                                    }
                                                    isLevelTwo={true}
                                                    setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                                    setIsLevelThreeMenuOpen={
                                                        setIsLevelThreeMenuOpen
                                                    }
                                                    isLevelTwoMenuOpen={isLevelTwoMenuOpen}
                                                    isLevelThreeMenuOpen={isLevelThreeMenuOpen}
                                                    setFeatureImageMobileLevelTwo={
                                                        setFeatureImageMobileLevelTwo
                                                    }
                                                    setFeatureImageMobileLevelThree={
                                                        setFeatureImageMobileLevelThree
                                                    }
                                                    setFeatureImageMobileLevelFour={
                                                        setFeatureImageMobileLevelFour
                                                    }
                                                />
                                            )}
                                        </>
                                    )}

                                {/* level 4 */}
                                {linksForLevelThreeMenu?.length > 0 && (
                                    <>
                                        {linksForLevelThreeMenu?.length > 0 && (
                                            <ListMenuContentInner
                                                items={linksForLevelThreeMenu}
                                                onClose={onClose}
                                                isMobileMenu={true}
                                                setMenuToggle={setMenuToggle}
                                                setfeatureImage={setfeatureImage}
                                                selectedLevelOneMenu={selectedLevelOneMenu}
                                                isLevelTwo={false}
                                                selectedLevelTwoMenu={selectedLevelTwoMenu}
                                                setSelectedLevelTwoMenu={setSelectedLevelTwoMenu}
                                                setLinksForLevelThreeMenu={
                                                    setLinksForLevelThreeMenu
                                                }
                                                setLinksForLevelTwoMenu={setLinksForLevelTwoMenu}
                                                setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                                setIsLevelThreeMenuOpen={setIsLevelThreeMenuOpen}
                                                isLevelTwoMenuOpen={isLevelTwoMenuOpen}
                                                isLevelThreeMenuOpen={isLevelThreeMenuOpen}
                                                setFeatureImageMobileLevelTwo={
                                                    setFeatureImageMobileLevelTwo
                                                }
                                                setFeatureImageMobileLevelThree={
                                                    setFeatureImageMobileLevelThree
                                                }
                                                setFeatureImageMobileLevelFour={
                                                    setFeatureImageMobileLevelFour
                                                }
                                            />
                                        )}
                                    </>
                                )}
                            </div>

                            {/* action menu */}
                            <Box {...styles.actionMenu}>
                                {isRegistered ? (
                                    <List {...styles.actionMenuItem}>
                                        <Link
                                            as={RouteLink}
                                            to="/account/info"
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.account_settings',
                                                defaultMessage: 'Account Settings'
                                            })}
                                        </Link>
                                    </List>
                                ) : (
                                    <List {...styles.actionMenuItem}>
                                        <Link
                                            // href="/account/"
                                            {...styles.actionMenuItemLink}
                                            onClick={handleOnAccountClick}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.account_register',
                                                defaultMessage: 'Account/Register'
                                            })}
                                        </Link>
                                    </List>
                                )}
                                <List {...styles.actionMenuItem}>
                                    <Box>
                                        <Link
                                            onClick={handleTrackOrderClick}
                                            {...styles.actionMenuItemLink}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.track_my_order',
                                                defaultMessage: 'Track My Order'
                                            })}
                                        </Link>
                                        <Link
                                            href="/storelocator/"
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.find_a_store',
                                                defaultMessage: 'Find a store'
                                            })}
                                        </Link>
                                    </Box>
                                </List>
                                <List {...styles.actionMenuItem}>
                                    <Box>
                                        <Link
                                            to="/help/"
                                            as={RouteLink}
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.customer_service',
                                                defaultMessage: 'Customer Service'
                                            })}
                                        </Link>
                                        <Link
                                            to="/content/returns-exchanges.html"
                                            as={RouteLink}
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.returns',
                                                defaultMessage: 'Returns'
                                            })}
                                        </Link>
                                    </Box>
                                </List>
                                <List {...styles.actionMenuItem}>
                                    <Box>
                                        <Link
                                            to="/sitemap"
                                            as={RouteLink}
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.site_map',
                                                defaultMessage: 'Site Map'
                                            })}
                                        </Link>
                                        <Link
                                            to="/content/privacy-policy.html"
                                            as={RouteLink}
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.privacy_policy',
                                                defaultMessage: 'Privacy Policy'
                                            })}
                                        </Link>
                                        {isRegistered && (
                                            <Link
                                                {...styles.actionMenuItemLink}
                                                onClick={onSignoutClick}
                                                sx={{display: 'block'}}
                                            >
                                                {intl.formatMessage({
                                                    id: 'header.link.signout',
                                                    defaultMessage: 'Sign Out'
                                                })}
                                            </Link>
                                        )}
                                    </Box>
                                </List>
                            </Box>

                            {/* level 1 feature image */}
                            {root?.[itemsKey] && !Object.keys(linksForContentComp)?.length > 0 && (
                                <Box
                                    {...styles.mobileFeatureImage}
                                    dangerouslySetInnerHTML={{__html: featureImage}}
                                ></Box>
                            )}

                            {/* level 2 feature image */}
                            {Object.keys(linksForContentComp)?.length > 0 &&
                                !linksForLevelTwoMenu?.length > 0 && (
                                    <>
                                        {Object.keys(linksForContentComp)?.length > 0 && (
                                            <Box
                                                {...styles.mobileFeatureImage}
                                                dangerouslySetInnerHTML={{
                                                    __html: featureImageMobileLevelTwo
                                                }}
                                            ></Box>
                                        )}
                                    </>
                                )}

                            {/* level 3feature image  */}
                            {linksForLevelTwoMenu?.length > 0 &&
                                !linksForLevelThreeMenu?.length > 0 && (
                                    <>
                                        {linksForLevelTwoMenu?.length > 0 && (
                                            <Box
                                                {...styles.mobileFeatureImage}
                                                dangerouslySetInnerHTML={{
                                                    __html: featureImageMobileLevelThree
                                                }}
                                            ></Box>
                                        )}
                                    </>
                                )}

                            {/* level 4 feature image */}
                            {linksForLevelThreeMenu?.length > 0 && (
                                <>
                                    {linksForLevelThreeMenu?.length > 0 && (
                                        <Box
                                            {...styles.mobileFeatureImage}
                                            dangerouslySetInnerHTML={{
                                                __html: featureImageMobileLevelFour
                                            }}
                                        ></Box>
                                    )}
                                </>
                            )}
                        </DrawerBody>
                    </DrawerContent>
                </DrawerOverlay>
            </Drawer>
            <AuthModal {...authModal} />
            <TrackOrderModal {...trackOrderModal} />
        </>
    )
}

DrawerMenu.displayName = 'DrawerMenu'

DrawerMenu.propTypes = {
    root: PropTypes.object,
    /**
     * The opened state of the drawer.
     */
    isOpen: PropTypes.bool,
    /**
     * Function called when the drawer is dismissed.
     */
    onClose: PropTypes.func,
    /**
     * Function called when the drawer logo is clicked.
     */
    onLogoClick: PropTypes.func,
    /**
     * Customize the property representing the items.
     */
    itemsKey: PropTypes.string,
    /**
     * Cusomtize the property representing the items count.
     */
    itemsCountKey: PropTypes.string,
    /**
     * Component to be rendered for each individual menu item.
     */
    isStickyActive: PropTypes.oneOf(true, false),
    promoBannerInHeaderIsActive: PropTypes.oneOf(true, false),
    setMenuToggle: PropTypes.func,
    menuToggle: PropTypes.bool,
    itemComponent: PropTypes.any
}

export {DrawerMenu}
